import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Input } from "reactstrap";
import { useContextualHelp } from "../../components/Help/ContextualHelp";
import { startGuidedTour } from "../../components/Help/GuidedTour";
import {
  createReportTemplate,
  createReportTemplateVersion,
  getReportTemplate,
  getReportTemplateLookups,
  publishReportTemplate,
  saveReportTemplateBasic,
  saveReportTemplateSections,
  saveReportTemplateStyles,
} from "../../helpers/backend_helper";
import { ErrorNotify } from "../../shared/Alert";
import "./ReportTemplateCreate.scss";

const steps = [
  "Basic Information",
  "Report Sections",
  "Content & Placeholders",
  "Styling & Branding",
  "Review & Publish",
];
const reportLayoutDefaults = {
  header: {
    enabled: true,
    left: "{{Firm.Name}}\n{{Firm.Description}}",
    right: "{{Firm.Address}}\n{{Firm.Phone}}",
    showDivider: true,
  },
  cover: {
    enabled: true,
    title: "{{Report.TemplateName}}",
    periodLabel: "For the Period",
    period: "{{Engagement.PeriodStart}} - {{Engagement.PeriodEnd}}",
  },
  tableOfContents: { enabled: true, title: "CONTENTS", showPageNumbers: true },
  footer: { text: "{{Firm.Name}} | Confidential" },
};

const parseConfiguration = (value, fallback = {}) => {
  try {
    return value ? { ...fallback, ...JSON.parse(value) } : fallback;
  } catch {
    return fallback;
  }
};

const reportLayout = (value) => {
  const layout = parseConfiguration(value, reportLayoutDefaults);
  return {
    ...reportLayoutDefaults,
    ...layout,
    header: { ...reportLayoutDefaults.header, ...(layout.header || {}) },
    cover: { ...reportLayoutDefaults.cover, ...(layout.cover || {}) },
    tableOfContents: {
      ...reportLayoutDefaults.tableOfContents,
      ...(layout.tableOfContents || {}),
    },
    footer: { ...reportLayoutDefaults.footer, ...(layout.footer || {}) },
  };
};
const blank = {
  id: 0,
  guid: null,
  name: "",
  description: "",
  reportTypeId: "",
  applicableStandardId: "",
  workTypeId: "",
  defaultLanguage: "en-US",
  versionNo: 1,
  status: "Draft",
  usageCount: 0,
  updatedAt: null,
  sections: [],
  style: {
    pageSize: "Letter",
    orientation: "Portrait",
    marginTop: 0.75,
    marginBottom: 0.75,
    marginLeft: 0.75,
    marginRight: 0.75,
    primaryFont: "Arial",
    bodyFontSize: 10,
    headingFont: "Arial",
    headingColor: "#1A56DB",
    primaryColor: "#1A56DB",
    secondaryColor: "#0F172A",
    headerEnabled: true,
    footerEnabled: true,
    showPageNumbers: true,
    configurationJson: JSON.stringify(reportLayoutDefaults),
  },
};

const uid = () =>
  crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

const resolve = (text = "") =>
  text.replace(/{{\s*([^}]+)\s*}}/g, (_, key) => `{{${key}}}`);

const blockConfiguration = (type) =>
  ({
    Heading: {
      level: "H2",
      alignment: "Left",
      font: "Arial",
      fontSize: 16,
      textColor: "#1A56DB",
      topSpacing: 16,
      bottomSpacing: 12,
    },
    Paragraph: {
      alignment: "Left",
      font: "Arial",
      fontSize: 12,
      textColor: "#334155",
      lineHeight: "1.6",
      topSpacing: 8,
      bottomSpacing: 12,
    },
    KeyValueGrid: {
      rows: [
        { label: "Reviewer", value: "{{Engagement.PartnerName}}" },
        { label: "Firm", value: "{{Firm.Name}}" },
      ],
      rowSource: "StaticRows",
      labelColumnWidth: 35,
      valueColumnWidth: 65,
      labelBold: true,
      labelColor: "#1F365C",
      valueColor: "#334155",
      showRowBorder: true,
      topSpacing: 8,
      bottomSpacing: 12,
    },
    DataTable: {
      dataSource: "Custom",
      columns: ["Column 1", "Column 2"],
      rows: [["", ""]],
      headerBackground: "#F3F6FB",
      headerColor: "#1F365C",
      alternateRowColor: "#FFFFFF",
      showBorders: true,
      topSpacing: 8,
      bottomSpacing: 12,
    },
    BulletList: {
      items: ["Item one", "Item two"],
      font: "Arial",
      fontSize: 12,
      textColor: "#334155",
      topSpacing: 8,
      bottomSpacing: 12,
    },
    NumberedList: {
      items: ["Item one", "Item two"],
      font: "Arial",
      fontSize: 12,
      textColor: "#334155",
      topSpacing: 8,
      bottomSpacing: 12,
    },
    PageBreak: {},
    Image: {
      sourceMode: "Upload",
      source: "",
      altText: "",
      alignment: "Center",
      maxWidth: 40,
      maintainAspectRatio: true,
      topSpacing: 8,
      bottomSpacing: 12,
    },
    Divider: {
      type: "Divider",
      style: "Solid",
      thickness: 1,
      color: "#94A3B8",
      height: 16,
      topSpacing: 8,
      bottomSpacing: 8,
    },
    Placeholder: {
      token: "",
      alignment: "Left",
      font: "Arial",
      fontSize: 12,
      textColor: "#334155",
    },
  }[type] || {});

const blockDisplayName = (type = "") => type.replace(/([A-Z])/g, " $1").trim();

const hydrateBlock = (block) => ({
  ...block,
  config:
    block.config ||
    parseConfiguration(
      block.configurationJson,
      blockConfiguration(block.blockType)
    ),
});

const serializeSections = (sections = []) =>
  sections.map((section) => ({
    ...section,
    blocks: (section.blocks || []).map(({ config, ...block }) => ({
      ...block,
      configurationJson: JSON.stringify(
        config || blockConfiguration(block.blockType)
      ),
    })),
  }));

const normalizeDesigner = (value) => {
  const style = { ...blank.style, ...(value?.style || {}) };
  return {
    ...blank,
    ...value,
    sections: Array.isArray(value?.sections)
      ? value.sections.map((section) => ({
          ...section,
          blocks: Array.isArray(section.blocks)
            ? section.blocks.map(hydrateBlock)
            : [],
        }))
      : [],
    style: {
      ...style,
      configurationJson: JSON.stringify(reportLayout(style.configurationJson)),
    },
  };
};

const presetBlock = (blockType, content = "", config = {}) => ({
  id: 0,
  guid: uid(),
  blockType,
  sortOrder: 0,
  content,
  config: { ...blockConfiguration(blockType), ...config },
  isEnabled: true,
});

const presetSection = (title, sectionType, blocks = [], options = {}) => ({
  id: 0,
  guid: uid(),
  sectionKey: title
    .replace(/[^A-Za-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .toUpperCase(),
  title,
  sectionType,
  sortOrder: 0,
  isEnabled: true,
  startOnNewPage: false,
  showInTableOfContents: true,
  isRequired: false,
  ...options,
  blocks: blocks.map((block, index) => ({ ...block, sortOrder: index })),
});

const placeholderKeys = (sections = []) => [
  ...new Set(
    sections.flatMap((section) =>
      (section.blocks || []).flatMap((block) =>
        [...(block.content || "").matchAll(/{{\s*([^}]+)\s*}}/g)].map((match) =>
          match[1].trim()
        )
      )
    )
  ),
];

const reportPreset = (reportType) => {
  const cover = (title) =>
    presetSection(
      "Cover Page",
      "CoverPage",
      [
        presetBlock("Heading", title, { level: "H1", alignment: "Center" }),
        presetBlock(
          "Paragraph",
          "{{Firm.Name}}\n{{Engagement.ClientName}}\n{{Report.GeneratedDate}}",
          { alignment: "Center" }
        ),
      ],
      { isRequired: true, showInTableOfContents: false }
    );

  const reportDetails = (title = "Report Details") =>
    presetSection(
      title,
      "EngagementInformation",
      [
        presetBlock("KeyValueGrid", "", {
          rows: [
            { label: "Firm", value: "{{Firm.Name}}" },
            { label: "Engagement", value: "{{Engagement.EngagementName}}" },
            {
              label: "Review period",
              value: "{{Engagement.PeriodStart}} – {{Engagement.PeriodEnd}}",
            },
            { label: "Reviewer", value: "{{Engagement.PartnerName}}" },
          ],
        }),
      ],
      { isRequired: true }
    );
  switch ((reportType || "").trim().toLowerCase()) {
    case "final report":
      return [
        cover("PEER REVIEW FINAL REPORT"),
        reportDetails(),
        presetSection(
          "Executive Summary",
          "StandardContent",
          [
            presetBlock(
              "Paragraph",
              "Provide the overall outcome, scope, and key observations from the review."
            ),
          ],
          { isRequired: true }
        ),
        presetSection("Findings Summary", "Findings", [
          presetBlock("DataTable", "", {
            dataSource: "Findings",
            columns: ["Finding", "Category", "Severity", "Status"],
            rows: [
              [
                "{{Findings.Title}}",
                "{{Findings.Category}}",
                "{{Findings.Severity}}",
                "{{Findings.Status}}",
              ],
            ],
          }),
        ]),
        presetSection("Detailed Findings", "Findings", [
          presetBlock("Placeholder", "{{Findings.List}}", {
            token: "{{Findings.List}}",
          }),
        ]),
        presetSection(
          "Conclusion",
          "Conclusion",
          [
            presetBlock(
              "Paragraph",
              "State the final peer-review conclusion and overall rating."
            ),
          ],
          { isRequired: true }
        ),
        presetSection(
          "Signatures",
          "Signature",
          [
            presetBlock("KeyValueGrid", "", {
              rows: [
                { label: "Reviewer", value: "{{Engagement.PartnerName}}" },
                { label: "Report date", value: "{{Report.GeneratedDate}}" },
              ],
            }),
          ],
          { isRequired: true }
        ),
      ];
    case "management letter":
      return [
        cover("MANAGEMENT LETTER"),
        reportDetails(),
        presetSection(
          "Management Summary",
          "StandardContent",
          [
            presetBlock(
              "Paragraph",
              "Summarize matters requiring management attention and the expected corrective actions."
            ),
          ],
          { isRequired: true }
        ),
        presetSection("Detailed Findings", "Findings", [
          presetBlock("DataTable", "", {
            dataSource: "Findings",
            columns: ["Finding", "Impact", "Recommendation", "Priority"],
            rows: [
              [
                "{{Findings.Title}}",
                "{{Findings.Impact}}",
                "{{Findings.Recommendation}}",
                "{{Findings.Severity}}",
              ],
            ],
          }),
        ]),
        presetSection("Management Response", "Recommendations", [
          presetBlock("DataTable", "", {
            dataSource: "Recommendations",
            columns: ["Finding", "Management response", "Owner", "Target date"],
            rows: [
              [
                "{{Findings.Title}}",
                "{{Findings.ManagementResponse}}",
                "{{Findings.Owner}}",
                "{{Findings.TargetDate}}",
              ],
            ],
          }),
        ]),
        presetSection("Corrective Action Plan", "Recommendations", [
          presetBlock("DataTable", "", {
            dataSource: "Recommendations",
            columns: ["Action", "Owner", "Due date", "Status"],
            rows: [
              [
                "{{Recommendations.Action}}",
                "{{Recommendations.Owner}}",
                "{{Recommendations.DueDate}}",
                "{{Recommendations.Status}}",
              ],
            ],
          }),
        ]),
        presetSection("Follow-up Requirements", "StandardContent", [
          presetBlock(
            "Paragraph",
            "Describe the follow-up, evidence, and timing required to close the agreed actions."
          ),
        ]),
        presetSection(
          "Acknowledgement",
          "Signature",
          [
            presetBlock(
              "Paragraph",
              "Management acknowledges receipt of this letter and the actions recorded above."
            ),
          ],
          { isRequired: true }
        ),
      ];
    case "findings report":
      return [
        cover("FINDINGS REPORT"),
        reportDetails("Review Context"),
        presetSection(
          "Findings Register",
          "Findings",
          [
            presetBlock("DataTable", "", {
              dataSource: "Findings",
              columns: ["Finding", "Category", "Severity", "Status"],
              rows: [
                [
                  "{{Findings.Title}}",
                  "{{Findings.Category}}",
                  "{{Findings.Severity}}",
                  "{{Findings.Status}}",
                ],
              ],
            }),
          ],
          { isRequired: true }
        ),
        presetSection("Finding Detail", "Findings", [
          presetBlock("Placeholder", "{{Findings.List}}", {
            token: "{{Findings.List}}",
          }),
        ]),
        presetSection("Risk Rating Summary", "Findings", [
          presetBlock("DataTable", "", {
            dataSource: "Findings",
            columns: ["Risk rating", "Count", "Open items"],
            rows: [
              ["High", "{{Findings.HighCount}}", "{{Findings.HighOpenCount}}"],
              [
                "Medium",
                "{{Findings.MediumCount}}",
                "{{Findings.MediumOpenCount}}",
              ],
              ["Low", "{{Findings.LowCount}}", "{{Findings.LowOpenCount}}"],
            ],
          }),
        ]),
        presetSection("Recommendations", "Recommendations", [
          presetBlock("Placeholder", "{{Findings.List}}", {
            token: "{{Findings.List}}",
          }),
        ]),
        presetSection(
          "Conclusion",
          "Conclusion",
          [
            presetBlock(
              "Paragraph",
              "Summarize the overall risk profile and the actions required."
            ),
          ],
          { isRequired: true }
        ),
      ];
    case "checklist summary":
      return [
        cover("CHECKLIST SUMMARY REPORT"),
        reportDetails("Review Details"),
        presetSection(
          "Checklist Completion Summary",
          "StandardContent",
          [
            presetBlock("KeyValueGrid", "", {
              rows: [
                { label: "Checklist", value: "{{Checklists.Name}}" },
                { label: "Score", value: "{{Checklists.Score}}" },
                { label: "Results", value: "{{Checklists.Results}}" },
              ],
            }),
          ],
          { isRequired: true }
        ),
        presetSection("Checklist Results", "StandardContent", [
          presetBlock("DataTable", "", {
            dataSource: "ChecklistResults",
            columns: ["Checklist item", "Result", "Evidence", "Status"],
            rows: [["Checklist item", "Result", "Evidence", "Status"]],
          }),
        ]),
        presetSection("Exceptions and Outstanding Items", "Findings", [
          presetBlock("Placeholder", "{{Checklists.Results}}", {
            token: "{{Checklists.Results}}",
          }),
        ]),
        presetSection("Required Follow-up Actions", "Recommendations", [
          presetBlock("DataTable", "", {
            dataSource: "Recommendations",
            columns: ["Action", "Owner", "Due date", "Status"],
            rows: [["Action", "Owner", "Due date", "Status"]],
          }),
        ]),
        presetSection(
          "Conclusion",
          "Conclusion",
          [
            presetBlock(
              "Paragraph",
              "Summarize checklist completion, exceptions, and required next actions."
            ),
          ],
          { isRequired: true }
        ),
      ];
    default:
      return [];
  }
};

const TemplateGuidance = ({ step, openHelp, startTour }) => (
  <aside
    className="report-template-create__guidance"
    aria-label="Template guidance and help"
  >
    <section>
      <header>
        <span>
          <i className="bx bx-book-open" />
        </span>
        <div>
          <h2>Guidance</h2>
          <p>Help for this template step.</p>
        </div>
      </header>
      <article>
        <b>
          <i className="bx bx-info-circle" /> About this step
        </b>
        <p>
          {step === 1
            ? "Add the report identity and the standards it will support. Required fields are marked with an asterisk."
            : step === 2
            ? "Define and organize report sections. Only enabled sections appear in the preview."
            : step === 3
            ? "Add content blocks and approved dynamic fields for the selected report section."
            : step === 4
            ? "Configure report branding and page presentation. Changes are shown in the preview."
            : "Review the template before publishing it for use in review templates."}
        </p>
      </article>
      <button
        type="button"
        onClick={() =>
          document.querySelector(".report-template-create__form input")?.focus()
        }
      >
        <i className="bx bx-check-circle" /> Complete required fields{" "}
        <i className="bx bx-chevron-right" />
      </button>
      <button
        type="button"
        onClick={() =>
          document
            .querySelector("[data-report-template-tour='preview']")
            ?.scrollIntoView({ behavior: "smooth", block: "nearest" })
        }
      >
        <i className="bx bx-show" /> Preview before publishing{" "}
        <i className="bx bx-chevron-right" />
      </button>
    </section>
    <section className="report-template-create__guided-tour">
      <h3>
        <i className="bx bx-wink-smile" /> New to PIERS?
      </h3>
      <p>Take a quick guided tour to learn this template workspace.</p>
      <Button outline type="button" onClick={startTour}>
        <i className="bx bx-play" /> Start Guided Tour
      </Button>
    </section>
    <section className="report-template-create__need-help">
      <h3>
        <i className="bx bx-help-circle" /> Need more help?
      </h3>
      <p>Open the Help Center for contextual guidance.</p>
      <Button outline type="button" onClick={openHelp}>
        Help Center
      </Button>
    </section>
  </aside>
);

const PreviewBlock = ({ block }) => {
  const config = hydrateBlock(block).config;
  const spacing = {
    marginTop: `${config.topSpacing || 0}px`,
    marginBottom: `${config.bottomSpacing || 0}px`,
  };
  const textStyle = {
    ...spacing,
    color: config.textColor,
    fontFamily: config.font,
    fontSize: config.fontSize ? `${config.fontSize}px` : undefined,
    lineHeight: config.lineHeight,
    textAlign: (config.alignment || "Left").toLowerCase(),
  };
  if (block.blockType === "PageBreak")
    return <hr className="report-template-create__preview-page-break" />;
  if (block.blockType === "Divider")
    return config.type === "Spacer" ? (
      <div style={{ height: `${config.height || 16}px`, ...spacing }} />
    ) : (
      <hr
        className={`report-template-create__preview-divider is-${(
          config.style || "Solid"
        ).toLowerCase()}`}
        style={{
          ...spacing,
          borderTopColor: config.color,
          borderTopWidth: `${config.thickness || 1}px`,
        }}
      />
    );
  if (block.blockType === "Heading") {
    const Tag =
      config.level === "H3" ? "h5" : config.level === "H1" ? "h3" : "h4";
    return (
      <Tag
        className={`report-template-create__preview-heading is-${(
          config.alignment || "Left"
        ).toLowerCase()}`}
        style={textStyle}
      >
        {resolve(block.content || "Heading")}
      </Tag>
    );
  }
  if (block.blockType === "KeyValueGrid")
    return (
      <table
        className="report-template-create__preview-grid"
        style={{
          ...spacing,
          borderCollapse: config.showRowBorder ? "collapse" : "separate",
        }}
      >
        <tbody>
          {(config.rows || []).map((row, index) => (
            <tr key={index}>
              <th
                style={{
                  color: config.labelColor,
                  fontWeight: config.labelBold ? 700 : 400,
                  width: `${config.labelColumnWidth || 35}%`,
                  borderColor: config.showRowBorder ? undefined : "transparent",
                }}
              >
                {row.label}
              </th>
              <td
                style={{
                  color: config.valueColor,
                  width: `${config.valueColumnWidth || 65}%`,
                  borderColor: config.showRowBorder ? undefined : "transparent",
                }}
              >
                {resolve(row.value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  if (block.blockType === "DataTable")
    return (
      <table
        className="report-template-create__preview-grid"
        style={{
          ...spacing,
          borderCollapse: config.showBorders ? "collapse" : "separate",
        }}
      >
        <thead>
          <tr>
            {(config.columns || []).map((column, index) => (
              <th
                key={index}
                style={{
                  background: config.headerBackground,
                  color: config.headerColor,
                  borderColor: config.showBorders ? undefined : "transparent",
                }}
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(config.rows || []).map((row, rowIndex) => (
            <tr
              key={rowIndex}
              style={{
                background: rowIndex % 2 ? config.alternateRowColor : undefined,
              }}
            >
              {row.map((value, index) => (
                <td
                  key={index}
                  style={{
                    borderColor: config.showBorders ? undefined : "transparent",
                  }}
                >
                  {resolve(value)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  if (block.blockType === "BulletList" || block.blockType === "NumberedList") {
    const Tag = block.blockType === "BulletList" ? "ul" : "ol";
    return (
      <Tag className="report-template-create__preview-list" style={textStyle}>
        {(config.items || []).map((item, index) => (
          <li key={index}>{resolve(item)}</li>
        ))}
      </Tag>
    );
  }
  if (block.blockType === "Image")
    return (
      <div
        className={`report-template-create__preview-image is-${(
          config.alignment || "Center"
        ).toLowerCase()}`}
        style={spacing}
      >
        {config.source ? (
          <img
            src={config.source}
            alt={config.altText || "Report asset"}
            style={{ maxWidth: `${config.maxWidth || 40}%` }}
          />
        ) : (
          <>
            <i className="bx bx-image" /> Image / logo placeholder
          </>
        )}
      </div>
    );
  return (
    <p className="report-template-create__body-copy" style={textStyle}>
      {resolve(block.content || config.token || "")}
    </p>
  );
};

export const ReportPreview = ({ template }) => {
  const previewRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const sections = (template.sections || [])
    .filter((section) => section.isEnabled)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const toc = sections.filter((section) => section.showInTableOfContents);
  const style = template.style || blank.style;
  const layout = reportLayout(style.configurationJson);
  useEffect(() => {
    const syncFullscreenState = () =>
      setIsFullscreen(document.fullscreenElement === previewRef.current);
    document.addEventListener("fullscreenchange", syncFullscreenState);
    return () =>
      document.removeEventListener("fullscreenchange", syncFullscreenState);
  }, []);

  const toggleFullscreen = () =>
    isFullscreen
      ? document.exitFullscreen?.()
      : previewRef.current?.requestFullscreen?.();

  const textLines = (value) =>
    resolve(value || "")
      .split("\n")
      .filter(Boolean);
  const hasHeader = style.headerEnabled && layout.header.enabled;
  const hasCover = layout.cover.enabled;
  return (
    <aside
      ref={previewRef}
      className="report-template-create__preview"
      data-report-template-tour="preview"
    >
      <header>
        <b>REPORT PREVIEW</b>
        <div>
          <button type="button" className="is-active">
            <i className="bx bx-desktop" />
          </button>
          <button type="button">
            <i className="bx bx-mobile-alt" />
          </button>
          <Button outline size="sm">
            <i className="bx bx-download" /> Download Sample
          </Button>
          <Button outline size="sm" onClick={toggleFullscreen}>
            <i
              className={`bx ${
                isFullscreen ? "bx-exit-fullscreen" : "bx-fullscreen"
              }`}
            />{" "}
            {isFullscreen ? "Exit Full Screen" : "Full Screen"}
          </Button>
        </div>
      </header>
      <article
        className="report-template-create__paper"
        style={{
          fontFamily: style.primaryFont,
          color: style.secondaryColor,
          borderTop: `4px solid ${style.primaryColor}`,
        }}
      >
        {hasHeader && (
          <>
            <div className="report-template-create__letterhead">
              <b>
                {textLines(layout.header.left).map((line, index) => (
                  <React.Fragment key={`${line}-${index}`}>
                    {index > 0 && <br />}
                    {line}
                  </React.Fragment>
                ))}
              </b>
              <em>
                {textLines(layout.header.right).map((line, index) => (
                  <React.Fragment key={`${line}-${index}`}>
                    {index > 0 && <br />}
                    {line}
                  </React.Fragment>
                ))}
              </em>
            </div>
            {layout.header.showDivider && <hr />}
          </>
        )}
        {hasCover && (
          <>
            <h2
              style={{
                fontFamily: style.headingFont,
                color: style.headingColor,
              }}
            >
              {resolve(layout.cover.title)}
            </h2>
            <p className="report-template-create__period">
              {resolve(layout.cover.periodLabel)}
              <br />
              <strong>{resolve(layout.cover.period)}</strong>
            </p>
          </>
        )}
        {layout.tableOfContents.enabled && toc.length > 0 && (
          <>
            <h3 style={{ color: style.primaryColor }}>
              {resolve(layout.tableOfContents.title)}
            </h3>
            <ol>
              {toc.map((section, index) => (
                <li key={section.guid}>
                  <span>{section.title}</span>
                  {layout.tableOfContents.showPageNumbers && <b>{index + 1}</b>}
                </li>
              ))}
            </ol>
          </>
        )}
        {sections.map((section) => (
          <section key={section.guid}>
            <h3 style={{ color: style.primaryColor }}>{section.title}</h3>
            {section.blocks
              .filter((block) => block.isEnabled)
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((block) => (
                <PreviewBlock key={block.guid} block={block} />
              ))}
          </section>
        ))}
        {style.footerEnabled && <footer>{resolve(layout.footer.text)}</footer>}
        <aside className="report-template-create__preview-data-note">
          <i className="bx bx-code-curly" /> Placeholder values are populated
          with live review data when the report is generated.
        </aside>
      </article>
    </aside>
  );
};

const Basic = ({ model, update, lookups, errors }) => (
  <>
    <h2>1. Basic Information</h2>
    <p>
      Build a reusable report template that can be used across review workflows.
    </p>
    <div className="report-template-create__fields">
      <label>
        Report Template Name <b>*</b>
        <Input
          invalid={!!errors.name}
          value={model.name}
          onChange={(e) => update("name", e.target.value)}
          placeholder="Enter report template name"
        />
        {errors.name && <small className="text-danger">{errors.name}</small>}
      </label>
      <label>
        Report Type <b>*</b>
        <Input
          invalid={!!errors.reportTypeId}
          type="select"
          value={model.reportTypeId || ""}
          onChange={(e) =>
            update(
              "reportTypeId",
              e.target.value ? Number(e.target.value) : null
            )
          }
        >
          <option value="">Select report type</option>
          {lookups.reportTypes?.map((x) => (
            <option key={x.value} value={x.value}>
              {x.label}
            </option>
          ))}
        </Input>
        {errors.reportTypeId && (
          <small className="text-danger">{errors.reportTypeId}</small>
        )}
      </label>
      <label className="is-wide">
        Description
        <Input
          type="textarea"
          rows="3"
          value={model.description || ""}
          onChange={(e) => update("description", e.target.value)}
          placeholder="Enter a description for this report template"
        />
      </label>
      <label>
        Applicable Standard <b>*</b>
        <Input
          invalid={!!errors.applicableStandardId}
          type="select"
          value={model.applicableStandardId || ""}
          onChange={(e) =>
            update(
              "applicableStandardId",
              e.target.value ? Number(e.target.value) : null
            )
          }
        >
          <option value="">Select applicable standard</option>
          {lookups.standards?.map((x) => (
            <option key={x.value} value={x.value}>
              {x.label}
            </option>
          ))}
        </Input>
        {errors.applicableStandardId && (
          <small className="text-danger">{errors.applicableStandardId}</small>
        )}
      </label>
      <label>
        Work Type
        <Input
          type="select"
          value={model.workTypeId || ""}
          onChange={(e) =>
            update("workTypeId", e.target.value ? Number(e.target.value) : null)
          }
        >
          <option value="">Select work type</option>
          {lookups.workTypes?.map((x) => (
            <option key={x.value} value={x.value}>
              {x.label}
            </option>
          ))}
        </Input>
      </label>
      <label>
        Default Language
        <Input
          type="select"
          value={model.defaultLanguage}
          onChange={(e) => update("defaultLanguage", e.target.value)}
        >
          <option value="en-US">English (US)</option>
        </Input>
      </label>
      <label>
        Version
        <Input value={model.versionNo} readOnly />
      </label>
    </div>
    <section className="report-template-create__usage">
      <article>
        <span>Used In Review Templates</span>
        <strong>{model.usageCount || 0}</strong>
        <span>{model.usageCount ? "In use" : "Not yet used"}</span>
      </article>
      <article>
        <span>Last Updated</span>
        <strong>
          {model.updatedAt
            ? new Date(model.updatedAt).toLocaleString()
            : "Not saved yet"}
        </strong>
      </article>
    </section>
  </>
);

const Sections = ({ model, setModel }) => {
  const [selected, setSelected] = useState(0);
  const [dragging, setDragging] = useState(null);
  const sections = model.sections || [];
  const current = sections[selected];

  const update = (patch) =>
    setModel((m) => ({
      ...m,
      sections: m.sections.map((section, index) =>
        index === selected ? { ...section, ...patch } : section
      ),
    }));

  const add = () => {
    const section = {
      id: 0,
      guid: uid(),
      sectionKey: "",
      title: `Section ${sections.length + 1}`,
      sectionType: "StandardContent",
      sortOrder: sections.length,
      isEnabled: true,
      showInTableOfContents: true,
      isRequired: false,
      blocks: [],
    };
    setModel((m) => ({ ...m, sections: [...m.sections, section] }));
    setSelected(sections.length);
  };

  const move = (from, to) => {
    if (from === null || from === to) return;
    setModel((m) => {
      const ordered = [...m.sections];
      const [moved] = ordered.splice(from, 1);
      ordered.splice(to, 0, moved);
      return {
        ...m,
        sections: ordered.map((section, index) => ({
          ...section,
          sortOrder: index,
        })),
      };
    });
    setSelected(to);
  };
  return (
    <>
      <div className="report-template-create__heading-row">
        <div>
          <h2>2. Report Sections</h2>
          <p>Drag a section card to reorder the report structure.</p>
        </div>
        <Button outline onClick={add}>
          <i className="bx bx-plus" /> Add Section
        </Button>
      </div>
      <div className="report-template-create__section-editor">
        <section>
          <b>SECTION STRUCTURE</b>
          {sections.map((section, index) => (
            <button
              type="button"
              draggable
              className={`${selected === index ? "is-selected" : ""}${
                dragging === index ? " is-dragging" : ""
              }`}
              key={section.guid}
              onClick={() => setSelected(index)}
              onDragStart={(event) => {
                event.dataTransfer.effectAllowed = "move";
                setDragging(index);
              }}
              onDragEnd={() => setDragging(null)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                move(dragging, index);
                setDragging(null);
              }}
            >
              <i className="bx bx-dots-vertical-rounded" aria-hidden="true" />
              <span>{index + 1}</span>
              <strong>
                <span className="report-template-create__section-title">
                  {section.title}
                </span>
                <small>{section.sectionType}</small>
              </strong>
              <em>{section.isRequired ? "Required" : "Optional"}</em>
            </button>
          ))}
          {!sections.length && (
            <p className="p-3">Add a section to start your report.</p>
          )}
        </section>
        {current && (
          <section className="report-template-create__section-properties">
            <b>SECTION PROPERTIES</b>
            <label>
              <span>
                Title <strong>*</strong>
              </span>
              <Input
                value={current.title}
                onChange={(e) => update({ title: e.target.value })}
              />
            </label>
            <label>
              Section Type
              <Input
                type="select"
                value={current.sectionType}
                onChange={(e) => update({ sectionType: e.target.value })}
              >
                {[
                  "CoverPage",
                  "TableOfContents",
                  "StandardContent",
                  "FirmInformation",
                  "EngagementInformation",
                  "Findings",
                  "Recommendations",
                  "Conclusion",
                  "Signature",
                  "Appendix",
                  "Custom",
                ].map((type) => (
                  <option key={type}>{type}</option>
                ))}
              </Input>
            </label>
            {[
              ["isEnabled", "Enable section"],
              ["showInTableOfContents", "Include in Table of Contents"],
              ["isRequired", "Required Section"],
              ["startOnNewPage", "Start on new page"],
            ].map(([field, label]) => (
              <div className="report-template-create__switch" key={field}>
                <span>{label}</span>
                <Input
                  type="checkbox"
                  checked={!!current[field]}
                  onChange={(e) => update({ [field]: e.target.checked })}
                />
              </div>
            ))}
            <footer>
              <Button
                className="report-template-create__delete-section"
                outline
                color="danger"
                disabled={current.isRequired}
                onClick={() => {
                  setModel((m) => ({
                    ...m,
                    sections: m.sections
                      .filter((_, i) => i !== selected)
                      .map((s, i) => ({ ...s, sortOrder: i })),
                  }));
                  setSelected(0);
                }}
              >
                Delete Section
              </Button>
            </footer>
          </section>
        )}
      </div>
    </>
  );
};

const blockTypes = [
  {
    type: "Heading",
    icon: "bx-text",
    description: "Add a section or sub-section heading.",
    content: "New heading",
  },
  {
    type: "Paragraph",
    icon: "bx-paragraph",
    description: "Add descriptive text or instructions.",
    content: "",
  },
  {
    type: "KeyValueGrid",
    icon: "bx-table",
    description: "Display labels and dynamic values in a table.",
    content: "Reviewer: {{Engagement.PartnerName}}\nFirm: {{Firm.Name}}",
  },
  {
    type: "DataTable",
    icon: "bx-grid-alt",
    description: "Insert a multi-column data table.",
    content: "",
  },
  {
    type: "BulletList",
    icon: "bx-list-ul",
    description: "Add a bulleted list.",
    content: "Item one\nItem two",
  },
  {
    type: "NumberedList",
    icon: "bx-list-ol",
    description: "Add a numbered list.",
    content: "Item one\nItem two",
  },
  {
    type: "PageBreak",
    icon: "bx-file-blank",
    description: "Start subsequent content on a new page.",
    content: "",
  },
  {
    type: "Image",
    icon: "bx-image",
    description: "Add a firm logo or other approved image.",
    content: "",
  },
  {
    type: "Divider",
    icon: "bx-minus",
    description: "Add a horizontal divider between content.",
    content: "",
  },
];

const BlockEditor = ({ source, onChange, onRemove }) => {
  const block = hydrateBlock(source);
  const config = block.config;

  const setConfig = (patch) =>
    onChange({ ...block, config: { ...config, ...patch } });

  const number = (event) => Number(event.target.value || 0);

  const textProperties = (lineHeight = false) => (
    <div className="report-template-create__block-fields report-template-create__block-fields--four">
      <label>
        Alignment
        <Input
          type="select"
          value={config.alignment || "Left"}
          onChange={(e) => setConfig({ alignment: e.target.value })}
        >
          <option>Left</option>
          <option>Center</option>
          <option>Right</option>
        </Input>
      </label>
      <label>
        Font
        <Input
          type="select"
          value={config.font || "Arial"}
          onChange={(e) => setConfig({ font: e.target.value })}
        >
          <option>Arial</option>
          <option>Calibri</option>
          <option>Georgia</option>
          <option>Inter</option>
        </Input>
      </label>
      <label>
        Size
        <Input
          type="number"
          min="8"
          max="48"
          value={config.fontSize || 12}
          onChange={(e) => setConfig({ fontSize: number(e) })}
        />
      </label>
      <label>
        Text color
        <Input
          type="color"
          value={config.textColor || "#334155"}
          onChange={(e) => setConfig({ textColor: e.target.value })}
        />
      </label>
      {lineHeight && (
        <label>
          Line height
          <Input
            type="select"
            value={config.lineHeight || "1.6"}
            onChange={(e) => setConfig({ lineHeight: e.target.value })}
          >
            <option>1.2</option>
            <option>1.4</option>
            <option>1.6</option>
            <option>1.8</option>
            <option>2.0</option>
          </Input>
        </label>
      )}
      <label>
        Top spacing
        <Input
          type="number"
          min="0"
          max="96"
          value={config.topSpacing || 0}
          onChange={(e) => setConfig({ topSpacing: number(e) })}
        />
      </label>
      <label>
        Bottom spacing
        <Input
          type="number"
          min="0"
          max="96"
          value={config.bottomSpacing || 0}
          onChange={(e) => setConfig({ bottomSpacing: number(e) })}
        />
      </label>
    </div>
  );

  const rowsEditor = (rows) => (
    <div className="report-template-create__grid-editor">
      {rows.map((row, index) => (
        <div className="report-template-create__grid-row" key={index}>
          <Input
            value={row.label}
            onChange={(e) =>
              setConfig({
                rows: rows.map((item, i) =>
                  i === index ? { ...item, label: e.target.value } : item
                ),
              })
            }
            placeholder="Label"
          />
          <Input
            value={row.value}
            onChange={(e) =>
              setConfig({
                rows: rows.map((item, i) =>
                  i === index ? { ...item, value: e.target.value } : item
                ),
              })
            }
            placeholder="Value or {{Placeholder}}"
          />
          <button
            type="button"
            onClick={() =>
              setConfig({ rows: rows.filter((_, i) => i !== index) })
            }
            aria-label="Remove row"
          >
            <i className="bx bx-trash" />
          </button>
        </div>
      ))}
      <Button
        outline
        size="sm"
        onClick={() => setConfig({ rows: [...rows, { label: "", value: "" }] })}
      >
        <i className="bx bx-plus" /> Add row
      </Button>
    </div>
  );
  let editor;
  if (block.blockType === "Heading")
    editor = (
      <div className="report-template-create__block-fields">
        <label>
          Heading text
          <Input
            value={block.content || ""}
            onChange={(e) => onChange({ ...block, content: e.target.value })}
          />
        </label>
        <div className="report-template-create__block-fields report-template-create__block-fields--four">
          <label>
            Heading level
            <Input
              type="select"
              value={config.level || "H2"}
              onChange={(e) => setConfig({ level: e.target.value })}
            >
              <option>H1</option>
              <option>H2</option>
              <option>H3</option>
            </Input>
          </label>
          {textProperties(false)}
        </div>
      </div>
    );
  else if (block.blockType === "KeyValueGrid")
    editor = (
      <>
        <p className="report-template-create__block-help">
          Configure each label and dynamic value, then set the grid
          presentation.
        </p>
        {rowsEditor(config.rows || [])}
        <div className="report-template-create__block-fields report-template-create__block-fields--four">
          <label>
            Row source
            <Input
              type="select"
              value={config.rowSource || "StaticRows"}
              onChange={(e) => setConfig({ rowSource: e.target.value })}
            >
              <option>StaticRows</option>
              <option>DynamicData</option>
            </Input>
          </label>
          <label>
            Label width (%)
            <Input
              type="number"
              min="10"
              max="90"
              value={config.labelColumnWidth || 35}
              onChange={(e) =>
                setConfig({
                  labelColumnWidth: number(e),
                  valueColumnWidth: 100 - number(e),
                })
              }
            />
          </label>
          <label>
            Value width (%)
            <Input
              type="number"
              readOnly
              value={config.valueColumnWidth || 65}
            />
          </label>
          <label>
            Label color
            <Input
              type="color"
              value={config.labelColor || "#1F365C"}
              onChange={(e) => setConfig({ labelColor: e.target.value })}
            />
          </label>
          <label>
            Value color
            <Input
              type="color"
              value={config.valueColor || "#334155"}
              onChange={(e) => setConfig({ valueColor: e.target.value })}
            />
          </label>
          <label className="report-template-create__inline-check">
            <Input
              type="checkbox"
              checked={!!config.labelBold}
              onChange={(e) => setConfig({ labelBold: e.target.checked })}
            />{" "}
            Bold labels
          </label>
          <label className="report-template-create__inline-check">
            <Input
              type="checkbox"
              checked={!!config.showRowBorder}
              onChange={(e) => setConfig({ showRowBorder: e.target.checked })}
            />{" "}
            Show row borders
          </label>
        </div>
      </>
    );
  else if (block.blockType === "DataTable")
    editor = (
      <div className="report-template-create__block-fields">
        <div className="report-template-create__block-fields report-template-create__block-fields--four">
          <label>
            Data source
            <Input
              type="select"
              value={config.dataSource || "Custom"}
              onChange={(e) => setConfig({ dataSource: e.target.value })}
            >
              <option>Custom</option>
              <option>Findings</option>
              <option>ChecklistResults</option>
              <option>Recommendations</option>
            </Input>
          </label>
          <label>
            Column count
            <Input
              type="number"
              min="1"
              max="8"
              value={(config.columns || []).length}
              onChange={(e) => {
                const count = number(e);
                const columns = Array.from(
                  { length: count },
                  (_, i) => config.columns?.[i] || `Column ${i + 1}`
                );
                setConfig({
                  columns,
                  rows: (config.rows || []).map((row) =>
                    columns.map((_, i) => row[i] || "")
                  ),
                });
              }}
            />
          </label>
          <label className="report-template-create__inline-check">
            <Input
              type="checkbox"
              checked={!!config.showBorders}
              onChange={(e) => setConfig({ showBorders: e.target.checked })}
            />{" "}
            Show borders
          </label>
          <label>
            Header background
            <Input
              type="color"
              value={config.headerBackground || "#F3F6FB"}
              onChange={(e) => setConfig({ headerBackground: e.target.value })}
            />
          </label>
          <label>
            Header text color
            <Input
              type="color"
              value={config.headerColor || "#1F365C"}
              onChange={(e) => setConfig({ headerColor: e.target.value })}
            />
          </label>
          <label>
            Alternate row color
            <Input
              type="color"
              value={config.alternateRowColor || "#FFFFFF"}
              onChange={(e) => setConfig({ alternateRowColor: e.target.value })}
            />
          </label>
        </div>
        <label>
          Column names (comma separated)
          <Input
            value={(config.columns || []).join(", ")}
            onChange={(e) =>
              setConfig({
                columns: e.target.value
                  .split(",")
                  .map((x) => x.trim())
                  .filter(Boolean),
              })
            }
          />
        </label>
        <label>
          Rows (one row per line; values separated with |)
          <Input
            type="textarea"
            rows="4"
            value={(config.rows || []).map((row) => row.join(" | ")).join("\n")}
            onChange={(e) =>
              setConfig({
                rows: e.target.value
                  .split("\n")
                  .filter(Boolean)
                  .map((row) => row.split("|").map((value) => value.trim())),
              })
            }
          />
        </label>
      </div>
    );
  else if (
    block.blockType === "BulletList" ||
    block.blockType === "NumberedList"
  )
    editor = (
      <div className="report-template-create__block-fields">
        <label>
          List items (one per line)
          <Input
            type="textarea"
            rows="5"
            value={(config.items || []).join("\n")}
            onChange={(e) =>
              setConfig({ items: e.target.value.split("\n").filter(Boolean) })
            }
          />
        </label>
        {textProperties(false)}
      </div>
    );
  else if (block.blockType === "Image")
    editor = (
      <div className="report-template-create__block-fields">
        <div className="report-template-create__block-fields report-template-create__block-fields--four">
          <label>
            Image source
            <Input
              type="select"
              value={config.sourceMode || "Upload"}
              onChange={(e) => setConfig({ sourceMode: e.target.value })}
            >
              <option>Upload</option>
              <option>SystemLibrary</option>
              <option>ImageUrl</option>
            </Input>
          </label>
          <label>
            Alignment
            <Input
              type="select"
              value={config.alignment || "Center"}
              onChange={(e) => setConfig({ alignment: e.target.value })}
            >
              <option>Left</option>
              <option>Center</option>
              <option>Right</option>
            </Input>
          </label>
          <label>
            Max width (%)
            <Input
              type="number"
              min="10"
              max="100"
              value={config.maxWidth || 40}
              onChange={(e) => setConfig({ maxWidth: number(e) })}
            />
          </label>
          <label className="report-template-create__inline-check">
            <Input
              type="checkbox"
              checked={!!config.maintainAspectRatio}
              onChange={(e) =>
                setConfig({ maintainAspectRatio: e.target.checked })
              }
            />{" "}
            Maintain aspect ratio
          </label>
        </div>
        <label>
          Image URL
          <Input
            value={config.source || ""}
            onChange={(e) => setConfig({ source: e.target.value })}
            placeholder="https://..."
          />
        </label>
        <label>
          Alt text
          <Input
            value={config.altText || ""}
            onChange={(e) => setConfig({ altText: e.target.value })}
          />
        </label>
      </div>
    );
  else if (block.blockType === "Divider")
    editor = (
      <div className="report-template-create__block-fields report-template-create__block-fields--four">
        <label>
          Type
          <Input
            type="select"
            value={config.type || "Divider"}
            onChange={(e) => setConfig({ type: e.target.value })}
          >
            <option>Divider</option>
            <option>Spacer</option>
          </Input>
        </label>
        <label>
          Line style
          <Input
            type="select"
            value={config.style || "Solid"}
            onChange={(e) => setConfig({ style: e.target.value })}
          >
            <option>Solid</option>
            <option>Dashed</option>
            <option>Dotted</option>
          </Input>
        </label>
        <label>
          Thickness
          <Input
            type="number"
            min="1"
            max="8"
            value={config.thickness || 1}
            onChange={(e) => setConfig({ thickness: number(e) })}
          />
        </label>
        <label>
          Color
          <Input
            type="color"
            value={config.color || "#94A3B8"}
            onChange={(e) => setConfig({ color: e.target.value })}
          />
        </label>
        <label>
          Spacer height
          <Input
            type="number"
            min="0"
            max="120"
            value={config.height || 16}
            onChange={(e) => setConfig({ height: number(e) })}
          />
        </label>
        <label>
          Top spacing
          <Input
            type="number"
            min="0"
            value={config.topSpacing || 0}
            onChange={(e) => setConfig({ topSpacing: number(e) })}
          />
        </label>
        <label>
          Bottom spacing
          <Input
            type="number"
            min="0"
            value={config.bottomSpacing || 0}
            onChange={(e) => setConfig({ bottomSpacing: number(e) })}
          />
        </label>
      </div>
    );
  else if (block.blockType === "PageBreak")
    editor = (
      <p className="report-template-create__block-help">
        <i className="bx bx-file" /> This block starts the next content block on
        a new page.
      </p>
    );
  else
    editor = (
      <div className="report-template-create__block-fields">
        <label className="report-template-create__block-single-field">
          {block.blockType === "Placeholder" ? "Dynamic field" : "Content"}
          <Input
            type="textarea"
            rows="4"
            value={block.content || config.token || ""}
            onChange={(e) => {
              const content = e.target.value;
              onChange({
                ...block,
                content,
                config:
                  block.blockType === "Placeholder"
                    ? { ...config, token: content }
                    : config,
              });
            }}
            placeholder="Content or {{Firm.Name}}"
          />
        </label>
        {textProperties(true)}
      </div>
    );
  return (
    <article className="report-template-create__typed-block">
      <header>
        <span>
          <i
            className={`bx ${
              blockTypes.find((type) => type.type === block.blockType)?.icon ||
              "bx-code"
            }`}
          />
        </span>
        <div>
          <b>{blockDisplayName(block.blockType)}</b>
          <small>
            {blockTypes.find((type) => type.type === block.blockType)
              ?.description || "Dynamic report content"}
          </small>
        </div>
        <Button outline color="danger" size="sm" onClick={onRemove}>
          <i className="bx bx-trash" /> Remove
        </Button>
      </header>
      {editor}
    </article>
  );
};

const Content = ({ model, setModel, lookups }) => {
  const [sectionIndex, setSectionIndex] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedType, setSelectedType] = useState("Paragraph");
  const [activeTab, setActiveTab] = useState("content");
  const section = model.sections?.[sectionIndex];
  const selectedBlock =
    blockTypes.find((block) => block.type === selectedType) || blockTypes[1];

  const updateBlocks = (blocks) =>
    setModel((m) => ({
      ...m,
      sections: m.sections.map((s, i) =>
        i === sectionIndex ? { ...s, blocks } : s
      ),
    }));

  const addBlock = () => {
    updateBlocks([
      ...section.blocks,
      {
        id: 0,
        guid: uid(),
        blockType: selectedBlock.type,
        sortOrder: section.blocks.length,
        content: selectedBlock.content,
        config: blockConfiguration(selectedBlock.type),
        isEnabled: true,
      },
    ]);
    setPickerOpen(false);
  };

  const addPlaceholder = (placeholder) => {
    const token = `{{${placeholder.key}}}`;
    updateBlocks([
      ...section.blocks,
      {
        id: 0,
        guid: uid(),
        blockType: "Placeholder",
        sortOrder: section.blocks.length,
        content: token,
        config: { token },
        isEnabled: true,
      },
    ]);
    setActiveTab("content");
  };
  if (!section)
    return (
      <>
        <h2>3. Content & Placeholders</h2>
        <p>Add a report section first.</p>
      </>
    );
  const categories = [
    ...new Set((lookups.placeholders || []).map((item) => item.category)),
  ];
  return (
    <>
      <h2>3. Content & Placeholders</h2>
      <p>
        Build the selected section with typed content blocks and approved
        dynamic fields.
      </p>
      <div className="report-template-create__content-tabs">
        <button
          type="button"
          className={activeTab === "content" ? "is-active" : ""}
          onClick={() => setActiveTab("content")}
        >
          Section Content
        </button>
        <button
          type="button"
          className={activeTab === "placeholders" ? "is-active" : ""}
          onClick={() => setActiveTab("placeholders")}
        >
          Placeholders Library
        </button>
      </div>
      <div className="report-template-create__select-section">
        <label>
          Select Section
          <Input
            type="select"
            value={sectionIndex}
            onChange={(e) => setSectionIndex(Number(e.target.value))}
          >
            {model.sections.map((s, i) => (
              <option value={i} key={s.guid}>
                {i + 1}. {s.title}
              </option>
            ))}
          </Input>
        </label>
        {activeTab === "content" && (
          <Button outline onClick={() => setPickerOpen(true)}>
            <i className="bx bx-plus" /> Add Block
          </Button>
        )}
      </div>
      {activeTab === "content" ? (
        <section className="report-template-create__typed-blocks">
          {section.blocks.map((source, index) => (
            <BlockEditor
              key={source.guid}
              source={source}
              onChange={(block) =>
                updateBlocks(
                  section.blocks.map((item, i) => (i === index ? block : item))
                )
              }
              onRemove={() =>
                updateBlocks(
                  section.blocks
                    .filter((_, i) => i !== index)
                    .map((block, i) => ({ ...block, sortOrder: i }))
                )
              }
            />
          ))}
          {!section.blocks.length && (
            <div className="report-template-create__empty-blocks">
              <i className="bx bx-layer-plus" />
              <b>No content blocks yet</b>
              <span>Use Add Block to build this section.</span>
            </div>
          )}
        </section>
      ) : (
        <section className="report-template-create__placeholder-library">
          {categories.map((category) => (
            <article key={category}>
              <h3>{category}</h3>
              {(lookups.placeholders || [])
                .filter((item) => item.category === category)
                .map((placeholder) => (
                  <button
                    type="button"
                    key={placeholder.key}
                    onClick={() => addPlaceholder(placeholder)}
                  >
                    <i className="bx bx-code-curly" />
                    <span>
                      <b>{placeholder.displayName}</b>
                      <small>{`{{${placeholder.key}}}`}</small>
                    </span>
                    <i className="bx bx-plus" />
                  </button>
                ))}
            </article>
          ))}
        </section>
      )}
      {pickerOpen && (
        <div
          className="report-template-create__block-picker-backdrop"
          role="presentation"
        >
          <section
            className="report-template-create__block-picker"
            role="dialog"
            aria-modal="true"
            aria-label="Add content block"
          >
            <header>
              <div>
                <h3>Add Block</h3>
                <p>Choose a block type to add to {section.title}.</p>
              </div>
              <button
                type="button"
                onClick={() => setPickerOpen(false)}
                aria-label="Close"
              >
                <i className="bx bx-x" />
              </button>
            </header>
            <main className="piers-page">
              <nav>
                {blockTypes.map((block) => (
                  <button
                    type="button"
                    key={block.type}
                    className={selectedType === block.type ? "is-selected" : ""}
                    onClick={() => setSelectedType(block.type)}
                  >
                    <i className={`bx ${block.icon}`} />
                    <span>
                      <b>{blockDisplayName(block.type)}</b>
                      <small>{block.description}</small>
                    </span>
                  </button>
                ))}
              </nav>
              <article>
                <b>Block Preview</b>
                <h4>{blockDisplayName(selectedBlock.type)}</h4>
                <p>{selectedBlock.description}</p>
                <div className="report-template-create__block-preview">
                  {selectedBlock.type === "Divider" ? (
                    <hr />
                  ) : selectedBlock.type === "PageBreak" ? (
                    <>
                      <i className="bx bx-file" /> Page break
                    </>
                  ) : (
                    selectedBlock.content ||
                    "Configure the block after adding it."
                  )}
                </div>
              </article>
            </main>
            <footer>
              <Button outline onClick={() => setPickerOpen(false)}>
                Cancel
              </Button>
              <Button color="primary" onClick={addBlock}>
                Add Block
              </Button>
            </footer>
          </section>
        </div>
      )}
    </>
  );
};

const Styling = ({ model, setModel }) => {
  const style = model.style || blank.style;
  const layout = reportLayout(style.configurationJson);

  const update = (field, value) =>
    setModel((m) => ({ ...m, style: { ...m.style, [field]: value } }));

  const updateLayout = (area, field, value) =>
    update(
      "configurationJson",
      JSON.stringify({ ...layout, [area]: { ...layout[area], [field]: value } })
    );
  return (
    <>
      <h2>4. Styling & Branding</h2>
      <p>
        All report layout settings below are persisted as template JSON and used
        by both the preview and generated PDF.
      </p>
      <div className="report-template-create__fields is-three">
        {[
          ["Primary Color", "primaryColor", "color"],
          ["Heading Color", "headingColor", "color"],
          ["Body Font", "primaryFont", "select"],
          ["Heading Font", "headingFont", "select"],
          ["Page Size", "pageSize", "select"],
          ["Orientation", "orientation", "select"],
        ].map(([label, field, type]) => (
          <label key={field}>
            {label}
            {type === "select" ? (
              <Input
                type="select"
                value={style[field]}
                onChange={(e) => update(field, e.target.value)}
              >
                {(field.includes("Font")
                  ? ["Arial", "Calibri", "Georgia"]
                  : field === "pageSize"
                  ? ["Letter", "A4"]
                  : ["Portrait", "Landscape"]
                ).map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </Input>
            ) : (
              <Input
                type="color"
                value={style[field]}
                onChange={(e) => update(field, e.target.value)}
              />
            )}
          </label>
        ))}
      </div>
      <h3 className="report-template-create__subheading">
        Header, cover and contents layout
      </h3>
      <div className="report-template-create__fields">
        <label>
          Header - left content
          <Input
            type="textarea"
            rows="3"
            value={layout.header.left}
            onChange={(e) => updateLayout("header", "left", e.target.value)}
          />
        </label>
        <label>
          Header - right content
          <Input
            type="textarea"
            rows="3"
            value={layout.header.right}
            onChange={(e) => updateLayout("header", "right", e.target.value)}
          />
        </label>
        <label>
          Cover title
          <Input
            value={layout.cover.title}
            onChange={(e) => updateLayout("cover", "title", e.target.value)}
          />
        </label>
        <label>
          Period label
          <Input
            value={layout.cover.periodLabel}
            onChange={(e) =>
              updateLayout("cover", "periodLabel", e.target.value)
            }
          />
        </label>
        <label>
          Period value
          <Input
            value={layout.cover.period}
            onChange={(e) => updateLayout("cover", "period", e.target.value)}
          />
        </label>
        <label>
          Footer content
          <Input
            value={layout.footer.text}
            onChange={(e) => updateLayout("footer", "text", e.target.value)}
          />
        </label>
        <label>
          Contents heading
          <Input
            value={layout.tableOfContents.title}
            onChange={(e) =>
              updateLayout("tableOfContents", "title", e.target.value)
            }
          />
        </label>
      </div>
      <div className="report-template-create__toggles">
        {[
          ["headerEnabled", "Show Header", null],
          ["footerEnabled", "Show Footer", null],
          ["showPageNumbers", "Show Page Numbers", null],
          ["showDivider", "Show Header Divider", "header"],
          ["enabled", "Show Cover", "cover"],
          ["enabled", "Show Contents", "tableOfContents"],
          ["showPageNumbers", "Show Contents Page Numbers", "tableOfContents"],
        ].map(([field, label, area]) => (
          <div key={`${area || "style"}-${field}`}>
            <span>{label}</span>
            <Input
              type="checkbox"
              checked={area ? !!layout[area][field] : !!style[field]}
              onChange={(e) =>
                area
                  ? updateLayout(area, field, e.target.checked)
                  : update(field, e.target.checked)
              }
            />
          </div>
        ))}
      </div>
    </>
  );
};

const ReviewPublish = ({ model, lookups, onEdit, onCreateVersion }) => {
  const enabledSections = model.sections.filter((section) => section.isEnabled);
  const blocks = enabledSections
    .flatMap((section) => section.blocks || [])
    .filter((block) => block.isEnabled);
  const keys = placeholderKeys(enabledSections);
  const knownKeys = new Set(
    (lookups.placeholders || []).map((placeholder) => placeholder.key)
  );

  const getLabel = (items, value, fallback) =>
    items?.find((item) => String(item.value) === String(value))?.label ||
    fallback;
  const isReady = Boolean(
    model.name?.trim() &&
      model.reportTypeId &&
      model.applicableStandardId &&
      enabledSections.length
  );
  const checks = [
    [
      Boolean(
        model.name?.trim() && model.reportTypeId && model.applicableStandardId
      ),
      "All required fields are completed",
    ],
    [enabledSections.length > 0, "At least one report section is added"],
    [
      keys.every((key) => !knownKeys.size || knownKeys.has(key)),
      "Placeholders are valid",
    ],
    [
      model.style?.headerEnabled && model.style?.footerEnabled,
      "Header and footer are configured",
    ],
    [
      isReady,
      model.status === "Published"
        ? "Report template is published"
        : "Report template is ready to publish",
    ],
  ];
  const summary = [
    ["Report Template Name", model.name || "Not specified"],
    ["Description", model.description || "No description provided"],
    [
      "Report Type",
      getLabel(lookups.reportTypes, model.reportTypeId, "Not specified"),
    ],
    [
      "Applicable Standard",
      getLabel(lookups.standards, model.applicableStandardId, "Not specified"),
    ],
    [
      "Work Type",
      getLabel(lookups.workTypes, model.workTypeId, "All Work Types"),
    ],
    [
      "Default Language",
      model.defaultLanguage === "en-US"
        ? "English (US)"
        : model.defaultLanguage,
    ],
    [
      "Include in Table of Contents",
      enabledSections.some((section) => section.showInTableOfContents)
        ? "Yes"
        : "No",
    ],
    ["Version", model.versionNo || 1],
    [
      "Last Updated",
      model.updatedAt
        ? new Date(model.updatedAt).toLocaleString()
        : "Not saved yet",
    ],
  ];
  return (
    <section className="report-template-create__review-publish">
      <div className="report-template-create__review-heading">
        <div>
          <h2>5. Review & Publish</h2>
          <p>
            Review your report template details, preview the output and publish
            when ready.
          </p>
        </div>
        <span
          className={`report-template-create__status is-${model.status.toLowerCase()}`}
        >
          {model.status}
        </span>
      </div>
      <section className="report-template-create__review">
        <header>
          REPORT TEMPLATE SUMMARY
          <Button outline size="sm" onClick={() => onEdit(1)}>
            <i className="bx bx-pencil" /> Edit
          </Button>
        </header>
        <div>
          {summary.map(([label, value]) => (
            <span key={label}>
              <b>{label}</b>
              {value}
            </span>
          ))}
        </div>
      </section>
      <section className="report-template-create__review-grid">
        <section>
          <b>INCLUDED COMPONENTS</b>
          {[
            ["Report Sections", `${enabledSections.length} sections`, 2],
            ["Content Blocks", `${blocks.length} blocks`, 3],
            ["Placeholders", `${keys.length} placeholders`, 3],
            [
              "Styling & Branding",
              model.style?.primaryColor ? "Custom" : "Default",
              4,
            ],
            [
              "Header & Footer",
              model.style?.headerEnabled || model.style?.footerEnabled
                ? "Enabled"
                : "Disabled",
              4,
            ],
          ].map(([label, value, targetStep]) => (
            <span key={label}>
              <b>{label}</b>
              <em>{value}</em>
              <Button outline size="sm" onClick={() => onEdit(targetStep)}>
                View
              </Button>
            </span>
          ))}
        </section>
        <section>
          <b>VALIDATION CHECKLIST</b>
          {checks.map(([valid, label]) => (
            <span className={valid ? "is-valid" : "is-invalid"} key={label}>
              <i
                className={`bx ${
                  valid ? "bx-check-circle" : "bx-error-circle"
                }`}
              />{" "}
              {label}
            </span>
          ))}
        </section>
      </section>
      <aside className="report-template-create__info">
        <i className="bx bx-info-circle" /> Once published, this report template
        will be available for use in review templates and inspections. You can
        create a new version anytime to update the template.
      </aside>
      {model.status === "Published" && (
        <aside className="report-template-create__published-note">
          <i className="bx bx-check-circle" /> This version is published. Create
          an editable draft to make changes.
        </aside>
      )}
      {model.status === "Published" && (
        <Button
          outline
          className="report-template-create__create-version"
          onClick={onCreateVersion}
        >
          Create editable draft version
        </Button>
      )}
    </section>
  );
};

const ReportTemplateCreate = ({ closeAction, onSaved, templateId }) => {
  const { openHelp } = useContextualHelp();
  const [step, setStep] = useState(1);
  const [model, setModel] = useState(blank);
  const [lookups, setLookups] = useState({});
  const [saveState, setSaveState] = useState("Not saved");
  const [basicErrors, setBasicErrors] = useState({});
  const dirty = useRef(false);
  useEffect(() => {
    getReportTemplateLookups().then(
      (r) => r.status && setLookups(r.data || {})
    );
    if (templateId)
      getReportTemplate(templateId).then(
        (r) => r.status && setModel(normalizeDesigner(r.data))
      );
  }, [templateId]);

  const setDirtyModel = (next) => {
    dirty.current = true;
    setModel(next);
  };

  const update = (field, value) => {
    setBasicErrors((current) => ({ ...current, [field]: undefined }));
    setDirtyModel((m) => {
      const next = { ...m, [field]: value };
      if (field !== "reportTypeId" || m.guid) return next;
      const selectedType = lookups.reportTypes?.find(
        (item) => Number(item.value) === Number(value)
      );
      return { ...next, sections: reportPreset(selectedType?.label) };
    });
  };

  const responseMessage = (response, fallback) =>
    response?.message ||
    Object.values(response?.errors || {})
      .flat()
      .filter(Boolean)
      .join(" ") ||
    fallback;

  const apply = (response) => {
    if (!response?.status || !response?.data)
      throw new Error(
        responseMessage(
          response,
          "The server did not return the saved report template."
        )
      );
    const saved = normalizeDesigner(response.data);
    setModel(saved);
    dirty.current = false;
    setSaveState("Saved just now");
    return saved;
  };

  const validateBasic = () => {
    const errors = {};
    if (!model.name?.trim()) errors.name = "Report Template Name is required.";
    if (!model.reportTypeId) errors.reportTypeId = "Report Type is required.";
    if (!model.applicableStandardId)
      errors.applicableStandardId = "Applicable Standard is required.";
    setBasicErrors(errors);
    if (Object.keys(errors).length) {
      ErrorNotify(
        "Complete the required Basic Information fields before saving."
      );
      return false;
    }
    return true;
  };

  const save = async ({ validate = false } = {}) => {
    if (validate && !validateBasic()) return null;
    setSaveState("Saving...");
    try {
      const snapshot = model;
      let current = snapshot;
      if (!current.guid) current = apply(await createReportTemplate(snapshot));
      const templateGuid = current.guid;
      current = apply(
        await saveReportTemplateBasic(templateGuid, {
          ...snapshot,
          guid: templateGuid,
        })
      );
      current = apply(
        await saveReportTemplateSections(
          templateGuid,
          serializeSections(snapshot.sections)
        )
      );
      current = apply(
        await saveReportTemplateStyles(templateGuid, snapshot.style)
      );
      onSaved?.();
      return current;
    } catch (e) {
      setSaveState("Save failed");
      ErrorNotify(e.message || "Unable to save the report template.");
      return null;
    }
  };

  const publish = async () => {
    if (!validateBasic()) {
      setStep(1);
      return;
    }
    const current = await save();
    if (!current) return;
    const response = await publishReportTemplate(current.guid);
    if (!response.status) {
      ErrorNotify(
        responseMessage(response, "Unable to publish the report template.")
      );
      return;
    }
    apply(response);
    setStep(5);
  };
  const content = [
    <Basic
      model={model}
      update={update}
      lookups={lookups}
      errors={basicErrors}
    />,
    <Sections model={model} setModel={setDirtyModel} />,
    <Content model={model} setModel={setDirtyModel} lookups={lookups} />,
    <Styling model={model} setModel={setDirtyModel} />,
    <>
      <h2>5. Review & Publish</h2>
      <p>
        {model.name || "Unnamed template"} · Version {model.versionNo} ·{" "}
        {model.sections.length} sections
      </p>
      {model.status === "Published" && (
        <Button
          outline
          onClick={async () =>
            apply(await createReportTemplateVersion(model.guid))
          }
        >
          Create editable draft version
        </Button>
      )}
    </>,
  ][step - 1];

  const createDraftVersion = async () => {
    try {
      apply(await createReportTemplateVersion(model.guid));
      setStep(1);
      onSaved?.();
    } catch (error) {
      ErrorNotify(
        error.message || "Unable to create an editable draft version."
      );
    }
  };
  const reviewContent = (
    <ReviewPublish
      model={model}
      lookups={lookups}
      onEdit={setStep}
      onCreateVersion={createDraftVersion}
    />
  );

  const startTour = () =>
    window.setTimeout(
      () =>
        startGuidedTour([
          {
            target: ".report-template-create__steps",
            title: "Template steps",
            description: "Use these steps to build the report template.",
          },
          {
            target: ".report-template-create__form",
            title: "Template details",
            description:
              "Complete the information and configuration for the active step.",
          },
          {
            target: "[data-report-template-tour='preview']",
            title: "Live preview",
            description:
              "The preview uses this same template model and updates as you edit.",
          },
        ]),
      0
    );

  const advance = () => {
    if (step < 5) setStep(step + 1);
  };

  const saveCurrentStep = () => save({ validate: true });
  const isPublished = model.status === "Published";
  return (
    <section className="report-template-create">
      <header className="report-template-create__header">
        <div>
          <h1 className="piers-page-title">{templateId ? "Edit" : "Create"} Report Template</h1>
          <p>
            Build a reusable report template that can be used across review
            workflows.
          </p>
        </div>
        <div className="report-template-create__header-actions">
          <span className="report-template-create__save-state">
            <i className="bx bx-check-circle" /> {saveState}
          </span>
          <Button outline onClick={closeAction}>
            Cancel
          </Button>
          <Button outline onClick={saveCurrentStep} disabled={isPublished}>
            Save Draft
          </Button>
          <Button
            color="primary"
            className="report-template-create__publish-action"
            onClick={isPublished ? createDraftVersion : publish}
          >
            {isPublished ? (
              "Create Draft Version"
            ) : (
              <>
                Save & Publish <i className="bx bx-chevron-down" />
              </>
            )}
          </Button>
          <Button
            className="report-template-create__close"
            onClick={closeAction}
            aria-label="Close report template"
          >
            <i className="bx bx-x" />
          </Button>
        </div>
      </header>
      <main className="piers-page">
        <aside className="report-template-create__sidebar">
          <nav className="report-template-create__steps">
            <b>CREATE REPORT TEMPLATE</b>
            {steps.map((title, index) => {
              const locked = !model.guid && index > 0;
              const complete = model.guid && index < step;
              return (
                <button
                  key={title}
                  type="button"
                  title={locked ? "Save Basic Information first" : title}
                  disabled={locked}
                  className={`${step === index + 1 ? "is-active" : ""}${
                    locked ? " is-locked" : ""
                  }${complete ? " is-complete" : ""}`}
                  onClick={() => !locked && setStep(index + 1)}
                >
                  <span>
                    {complete ? <i className="bx bx-check" /> : index + 1}
                  </span>
                  {title}
                </button>
              );
            })}
          </nav>
          <TemplateGuidance
            step={step}
            openHelp={openHelp}
            startTour={startTour}
          />
        </aside>
        <section className="report-template-create__workspace">
          <div className="report-template-create__form">
            {step === 5 ? reviewContent : content}
          </div>
          <ReportPreview template={model} />
        </section>
      </main>
      <footer className="report-template-create__footer">
        {step > 1 ? (
          <Button outline onClick={() => setStep(step - 1)}>
            <i className="bx bx-left-arrow-alt" /> Previous
            {step === 5 ? ": Styling & Branding" : ""}
          </Button>
        ) : (
          <span />
        )}
        <div>
          {step !== 5 && (
            <Button color="primary" onClick={saveCurrentStep}>
              Save
            </Button>
          )}
          {step < 5 && (
            <Button color="primary" onClick={advance} disabled={!model.guid}>
              Next: {steps[step]}
            </Button>
          )}
          {step === 5 && (
            <Button
              color="primary"
              onClick={isPublished ? createDraftVersion : publish}
            >
              {isPublished ? "Create Draft Version" : "Publish Report Template"}{" "}
              <i className={`bx ${isPublished ? "bx-copy-alt" : "bx-send"}`} />
            </Button>
          )}
        </div>
      </footer>
    </section>
  );
};
export default ReportTemplateCreate;
